import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BlobPreloader } from '../components/BlobPreloader';
import { 
  Upload, 
  FileAudio, 
  CheckCircle, 
  XCircle, 
  Mic, 
  Brain, 
  X, 
  AlertCircle,
  File,
  Trash2,
  CloudUpload,
  Zap,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Compact Analysis Progress Modal
const AnalysisProgressModal = ({ isOpen, totalFiles, completedFiles, failedFiles, currentStep, onClose, onDone }) => {
  if (!isOpen) return null;

  const percentage = totalFiles > 0 ? Math.round((completedFiles / totalFiles) * 100) : 0;
  const isComplete = completedFiles + failedFiles >= totalFiles && totalFiles > 0;
  const isProcessing = !isComplete && currentStep > 0;

  const steps = [
    { id: 1, name: 'Uploading to storage', model: 'GridFS' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-[380px] overflow-hidden">
        {/* Header with progress */}
        <div className={`p-6 ${isComplete ? 'bg-emerald-500' : 'bg-[#2eb4ac]'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {isComplete ? (
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Loader2 className="w-7 h-7 text-white animate-spin" />
                </div>
              )}
              <div>
                <h3 className="text-white font-bold text-lg">
                  {isComplete ? 'Complete!' : 'Processing...'}
                </h3>
                <p className="text-white/80 text-sm">
                  {isComplete 
                    ? `${completedFiles} of ${totalFiles} analyzed`
                    : `${completedFiles}/${totalFiles} files`}
                </p>
              </div>
            </div>
            <span className="text-3xl font-bold text-white">{percentage}%</span>
          </div>
          {/* Progress bar */}
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.max(percentage, isProcessing ? 5 : 0)}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="p-4 space-y-2">
          {steps.map((step) => {
            const isActive = currentStep === step.id;
            const isCompleted = isComplete || currentStep > step.id;
            
            return (
              <div 
                key={step.id}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                  isActive ? 'bg-[#2eb4ac]/10 border border-[#2eb4ac]/20' : 'bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  ) : isActive ? (
                    <Loader2 className="w-5 h-5 text-[#2eb4ac] animate-spin" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-slate-300" />
                  )}
                  <span className={`font-medium text-sm ${isActive ? 'text-[#2eb4ac]' : isCompleted ? 'text-slate-700' : 'text-slate-400'}`}>
                    {step.name}
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  isActive || isCompleted ? 'bg-[#2eb4ac]/10 text-[#2eb4ac]' : 'bg-slate-100 text-slate-400'
                }`}>
                  {step.model}
                </span>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4 flex gap-2">
          {isComplete ? (
            <button
              onClick={onDone}
              className="flex-1 py-3 bg-emerald-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              View Results
            </button>
          ) : (
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-slate-100 text-slate-600 font-medium rounded-xl hover:bg-slate-200 transition-colors"
            >
              Run in Background
            </button>
          )}
        </div>
        
        {/* Powered by */}
        <div className="px-4 pb-4 flex items-center justify-center gap-2 text-xs text-slate-400">
          <Zap className="w-3 h-3 text-amber-500" />
          <span>Powered by Groq • Hindi + Hinglish</span>
        </div>
      </div>
    </div>
  );
};

const CallUpload = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [agents, setAgents] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [selectedDataSource, setSelectedDataSource] = useState('');
  const [showCustomSourceInput, setShowCustomSourceInput] = useState(false);
  const [customSourceName, setCustomSourceName] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [manualCallDate, setManualCallDate] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisState, setAnalysisState] = useState({
    currentStep: 1,
    completedFiles: 0,
    failedFiles: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [agentsRes, sourcesRes] = await Promise.all([
        axios.get(`${API}/agents`),
        axios.get(`${API}/data-sources`)
      ]);
      setAgents(agentsRes.data);
      setDataSources(sourcesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    // Accept ANY audio file based on MIME or known extension; Whisper handles most formats.
    const knownExtensions = [
      '.mp3', '.wav', '.m4a', '.webm', '.ogg', '.oga', '.opus', '.flac',
      '.aac', '.aiff', '.aif', '.wma', '.amr', '.3gp', '.3gpp', '.mp4',
      '.mpga', '.mpeg', '.caf'
    ];
    const validFiles = fileArray.filter(file => {
      const name = (file.name || '').toLowerCase();
      const type = (file.type || '').toLowerCase();
      const hasAudioMime = type.startsWith('audio/');
      const hasKnownExt = knownExtensions.some(ext => name.endsWith(ext));
      // Some browsers don't set a MIME type for less common formats — fall back to extension.
      return hasAudioMime || hasKnownExt;
    });

    if (validFiles.length !== fileArray.length) {
      toast.error('Some files were skipped. Only audio files are supported.');
    }

    const newFiles = validFiles.map(file => ({
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      status: 'pending'
    }));

    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setUploadProgress({});
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    // Count files that need to be uploaded (exclude already successful ones)
    const filesToUpload = selectedFiles.filter(f => f.status !== 'success');
    const totalToProcess = filesToUpload.length;
    
    if (totalToProcess === 0) {
      toast.info('All files already uploaded');
      return;
    }

    setUploading(true);
    setShowAnalysisModal(true);
    setAnalysisState({ currentStep: 1, completedFiles: 0, failedFiles: 0, totalFiles: totalToProcess });
    
    let successCount = 0;
    let failCount = 0;

    for (const fileData of filesToUpload) {
      setUploadProgress(prev => ({
        ...prev,
        [fileData.id]: { status: 'uploading', progress: 0 }
      }));

      // Simulate step progression
      setAnalysisState(prev => ({ ...prev, currentStep: 1 }));

      try {
        const formData = new FormData();
        formData.append('file', fileData.file);
        if (selectedAgent) formData.append('agent_id', selectedAgent);
        if (selectedDataSource) formData.append('data_source', selectedDataSource);
        if (manualCallDate) {
          // Convert datetime-local value (e.g. "2026-04-23T15:30") to ISO with Z
          const isoDate = new Date(manualCallDate).toISOString();
          formData.append('call_date', isoDate);
        }

        // Step 1: Transcription (during upload)
        await axios.post(`${API}/calls/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(prev => ({
              ...prev,
              [fileData.id]: { status: 'uploading', progress }
            }));
            
            // Update step based on progress
            if (progress < 30) {
              setAnalysisState(prev => ({ ...prev, currentStep: 1 }));
            } else if (progress < 60) {
              setAnalysisState(prev => ({ ...prev, currentStep: 2 }));
            } else if (progress < 90) {
              setAnalysisState(prev => ({ ...prev, currentStep: 3 }));
            } else {
              setAnalysisState(prev => ({ ...prev, currentStep: 4 }));
            }
          }
        });

        setUploadProgress(prev => ({
          ...prev,
          [fileData.id]: { status: 'success', progress: 100 }
        }));

        setSelectedFiles(prev => 
          prev.map(f => f.id === fileData.id ? { ...f, status: 'success' } : f)
        );

        successCount++;
        setAnalysisState(prev => ({ ...prev, completedFiles: successCount, currentStep: 4 }));
      } catch (error) {
        console.error(`Error uploading ${fileData.name}:`, error);
        setUploadProgress(prev => ({
          ...prev,
          [fileData.id]: { status: 'error', error: error.response?.data?.detail || 'Upload failed' }
        }));

        setSelectedFiles(prev => 
          prev.map(f => f.id === fileData.id ? { ...f, status: 'error' } : f)
        );

        failCount++;
        setAnalysisState(prev => ({ ...prev, failedFiles: failCount }));
      }
    }

    setUploading(false);
    setAnalysisState(prev => ({ ...prev, currentStep: 4 }));

    if (successCount > 0 && !showAnalysisModal) {
      toast.success(`${successCount} call(s) uploaded · ready to analyze from Call Logs`);
    }
    if (failCount > 0 && !showAnalysisModal) {
      toast.error(`${failCount} call(s) failed to upload`);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <BlobPreloader size={88} label="Loading..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="call-upload-page">
      {/* Analysis Progress Modal */}
      <AnalysisProgressModal
        isOpen={showAnalysisModal}
        totalFiles={analysisState.totalFiles || selectedFiles.length}
        completedFiles={analysisState.completedFiles}
        failedFiles={analysisState.failedFiles}
        currentStep={analysisState.currentStep}
        onClose={() => setShowAnalysisModal(false)}
        onDone={() => {
          setShowAnalysisModal(false);
          navigate('/call-logs');
        }}
      />
      
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Upload Calls</h1>
        <p className="text-slate-500 mt-1">Upload sales call recordings. Run analysis later from <span className="text-[#2eb4ac] font-medium">Call Logs → Bulk Analyze</span> (up to 30 at a time).</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Upload Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* Drop Zone */}
          <div
            className={`relative bg-white rounded-2xl border-2 border-dashed p-8 transition-all duration-200 ${
              dragActive
                ? 'border-[#2eb4ac] bg-[#2eb4ac]/5'
                : 'border-slate-200 hover:border-[#2eb4ac]/50 hover:bg-slate-50/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".mp3,.wav,.m4a,.webm,.ogg,.oga,.opus,.flac,.aac,.aiff,.aif,.wma,.amr,.3gp,.3gpp,.mp4,.mpga,.mpeg,.caf,audio/*"
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              data-testid="file-input"
            />

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#2eb4ac]/10 to-[#2eb4ac]/5 flex items-center justify-center">
                <CloudUpload className="w-10 h-10 text-[#2eb4ac]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Drop your audio files here
              </h3>
              <p className="text-slate-500 mb-6">
                or click to browse from your computer
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2eb4ac] to-[#1e8a84] text-white font-semibold rounded-xl shadow-lg shadow-[#2eb4ac]/25 hover:shadow-[#2eb4ac]/40 transition-all"
                data-testid="select-files-button"
              >
                <Upload className="w-5 h-5" />
                Select Files
              </button>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {['MP3', 'WAV', 'M4A', 'WebM', 'OGG'].map((format) => (
                  <span key={format} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                    {format}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="selected-files-list">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Selected Files</h3>
                  <p className="text-sm text-slate-500">{selectedFiles.length} file(s) ready</p>
                </div>
                <button
                  onClick={clearAllFiles}
                  className="text-sm text-slate-500 hover:text-rose-600 transition-colors"
                  data-testid="clear-all-button"
                >
                  Clear All
                </button>
              </div>
              <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                {selectedFiles.map((fileData) => {
                  const progress = uploadProgress[fileData.id];
                  return (
                    <div key={fileData.id} className="px-6 py-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[#2eb4ac]/10 flex items-center justify-center flex-shrink-0">
                        <FileAudio className="w-5 h-5 text-[#2eb4ac]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{fileData.name}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-slate-500">{formatFileSize(fileData.size)}</span>
                          {progress && (
                            <>
                              {progress.status === 'uploading' && (
                                <span className="text-xs text-[#2eb4ac]">Uploading... {progress.progress}%</span>
                              )}
                              {progress.status === 'success' && (
                                <span className="text-xs text-emerald-600 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" /> Analyzed
                                </span>
                              )}
                              {progress.status === 'error' && (
                                <span className="text-xs text-rose-600 flex items-center gap-1">
                                  <XCircle className="w-3 h-3" /> {progress.error}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        {progress?.status === 'uploading' && (
                          <div className="mt-2 w-full bg-slate-100 rounded-full h-1.5">
                            <div
                              className="bg-[#2eb4ac] h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${progress.progress}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                      {!uploading && fileData.status !== 'success' && (
                        <button
                          onClick={() => removeFile(fileData.id)}
                          className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          data-testid={`remove-file-${fileData.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {fileData.status === 'success' && (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {selectedFiles.length > 0 && selectedFiles.some(f => f.status !== 'success') && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className={`w-full flex items-center justify-center gap-2 px-6 py-4 font-semibold rounded-xl transition-all ${
                uploading
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#2eb4ac] to-[#1e8a84] text-white shadow-lg shadow-[#2eb4ac]/25 hover:shadow-[#2eb4ac]/40'
              }`}
              data-testid="upload-analyze-button"
            >
              {uploading ? (
                <>
                  <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Brain className="w-5 h-5" />
                  Upload Call{selectedFiles.filter(f => f.status !== 'success').length !== 1 ? 's' : ''} ({selectedFiles.filter(f => f.status !== 'success').length})
                </>
              )}
            </button>
          )}

          {/* Success State - Go to Call Logs */}
          {selectedFiles.length > 0 && selectedFiles.every(f => f.status === 'success') && (
            <button
              onClick={() => navigate('/call-logs')}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all"
              data-testid="view-call-logs-button"
            >
              <CheckCircle className="w-5 h-5" />
              View in Call Logs
            </button>
          )}
        </div>

        {/* Sidebar - Configuration */}
        <div className="space-y-6">
          {/* Upload Configuration */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="upload-config">
            <h3 className="font-semibold text-slate-900 mb-4">Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Assign Agent</label>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac]"
                  data-testid="agent-select"
                >
                  <option value="">Select agent (optional)</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Data Source
                  <span className="ml-2 text-xs font-normal text-slate-400">Optional</span>
                </label>
                {!showCustomSourceInput ? (
                  <select
                    value={selectedDataSource}
                    onChange={(e) => {
                      if (e.target.value === '__add_custom__') {
                        setShowCustomSourceInput(true);
                        setCustomSourceName('');
                      } else {
                        setSelectedDataSource(e.target.value);
                      }
                    }}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac]"
                    data-testid="data-source-select"
                  >
                    <option value="">Select source</option>
                    {dataSources.map((s) => (
                      <option key={s.id} value={s.name}>{s.name}{!s.is_default ? ' (custom)' : ''}</option>
                    ))}
                    <option value="__add_custom__">+ Add custom source…</option>
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customSourceName}
                      onChange={(e) => setCustomSourceName(e.target.value)}
                      placeholder="e.g. Referral, Trade Show"
                      className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac]"
                      data-testid="custom-source-input"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        const name = customSourceName.trim();
                        if (!name) return;
                        try {
                          const res = await axios.post(`${API}/data-sources`, { name });
                          const newSrc = res.data;
                          setDataSources(prev => {
                            const exists = prev.find(s => s.id === newSrc.id);
                            return exists ? prev : [...prev, newSrc];
                          });
                          setSelectedDataSource(newSrc.name);
                          setShowCustomSourceInput(false);
                          setCustomSourceName('');
                          toast.success(`"${newSrc.name}" added`);
                        } catch (err) {
                          toast.error('Failed to add source');
                        }
                      }}
                      className="px-3 py-2 bg-[#2eb4ac] text-white text-sm font-medium rounded-xl hover:bg-[#239e98] transition-colors"
                      data-testid="save-custom-source"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowCustomSourceInput(false); setCustomSourceName(''); }}
                      className="px-3 py-2 bg-slate-100 text-slate-600 text-sm rounded-xl hover:bg-slate-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Call Date & Time
                  <span className="ml-2 text-xs font-normal text-slate-400">Optional override</span>
                </label>
                <input
                  type="datetime-local"
                  value={manualCallDate}
                  onChange={(e) => setManualCallDate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac]"
                  data-testid="call-date-input"
                />
                <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">
                  Auto-extracted from audio metadata when available. Fill this only to override.
                </p>
              </div>
            </div>
          </div>

          {/* Features Info */}
          <div className="bg-gradient-to-br from-[#2eb4ac]/5 to-transparent rounded-2xl border border-[#2eb4ac]/10 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">AI Analysis Features</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-[#2eb4ac]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mic className="w-3.5 h-3.5 text-[#2eb4ac]" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Speaker Detection</p>
                  <p className="text-xs text-slate-500">Automatic speaker diarization</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-[#2eb4ac]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Brain className="w-3.5 h-3.5 text-[#2eb4ac]" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Sentiment Analysis</p>
                  <p className="text-xs text-slate-500">Detect buyer interest & objections</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-[#2eb4ac]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3.5 h-3.5 text-[#2eb4ac]" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Smart Verdicts</p>
                  <p className="text-xs text-slate-500">Interested / Not Interested classification</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Note */}
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800 text-sm">Processing Time</p>
                <p className="text-xs text-amber-700 mt-1">
                  Each call takes ~30-60 seconds for transcription and analysis. Bulk uploads are processed sequentially.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallUpload;
